import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useFetchUbicacionProductoSimple } from "@/shared/actions/erp";
import ConceptosTrasladoSection from "../otros-conceptos-traslado/ConceptosTrasladoSection";
import ExcedenteFibraTraslado from "../otros-conceptos-traslado/ExcedenteFibraTraslado";
import { excedenteFibraDe } from "../otros-conceptos-traslado/excedente-fibra.utils";
import { aprobacionDe } from "../otros-conceptos-traslado/conceptos-traslado.utils";
import AgendamientoOrdenServicioVision360 from "@/erp/orden-servicio/orden-servicio-visita/components/save-agendamiento-orden-visita/AgendamientoOrdenServicioVision360";
import {
  ESTADOS_LLAMADAS_CHOICE,
  EstadosLlamadaEnumChoice,
  formatHour,
  gridSize,
  gridSizeMdLg12,
  gridSizeMdLg4,
  gridSizeMdLg6,
  parentescoMockupData,
  texto,
} from "@/shared";
import {
  CustomAutocomplete,
  CustomAutocompleteArrString,
  CustomTextArea,
  CustomTextField,
  CustomTextFieldNoForm,
  SingleButton,
  SingleIconButton,
  TabTexLabelCustomSpace,
  TabTextLabel,
} from "@/shared/components/ui";
import CustomCellphoneTextField from "@/shared/components/ui/CustomCellphoneTextField";
import ImagesWithZoom from "@/shared/components/ui/Images/ImagesWithZoom";
import { Agendamiento, Contrato } from "@/shared/interfaces";
import { TooltipIconEdit } from "@/erp/sale";
import { ServiceAndOthersPartPreagendamientoForm } from "@/erp/noc/agendamientonoc/shared/components";
import useSelectDateTimerPreScheduling from "@/erp/sale/component/calendario-preagendamiento-venta/useSelectDateTimerPreScheduling.store";
import { SaveFormDataAgendamiento } from "@/erp/operaciones/agendamiento/shared/interface/SaveFormDataAgendamiento.interface";
import { SelectDateTimerSchedulingPyl } from "@/erp/operaciones/agendamiento/shared/components/calendario-preagendamiento-pyl/SelectDateTimerSchedulingPyl";
import AgendamientoOrdenServicioUbicacionContrato from "./AgendamientoOrdenServicioUbicacionContrato";
import { Person } from "@mui/icons-material";
import { Box, Grid, Icon, Typography } from "@mui/material";
import ImageIcon from '@mui/icons-material/Image';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

export type AgendamientoSingleOrdenServicioTrasladoProps = {
  form: UseFormReturn<SaveFormDataAgendamiento>;
  shrink?: boolean;
  agendamiento: Agendamiento;
  isCorrectingData?: boolean;
  contrato: Contrato
};

const AgendamientoSingleOrdenServicioTraslado: React.FC<AgendamientoSingleOrdenServicioTrasladoProps> = ({
  form,
  shrink = true,
  isCorrectingData = false,
  agendamiento,
  contrato
}) => {
  ///* form --------------------
  const { errors } = form?.formState;
  // Ubicación anterior (origen) capturada al crear el traslado.
  const ubicacionAnterior = (agendamiento as any)?.observaciones?.ubicacion_anterior;
  // Asesor EVA que creó la orden de servicio de traslado + fecha/hora de creación.
  const asesorCreador = (agendamiento as any)?.user_create || "—";
  const _fechaCreacion = (agendamiento as any)?.fecha_creacion as string | undefined;
  const fechaCreado = _fechaCreacion ? _fechaCreacion.slice(0, 10) : "—";
  const horaCreado = _fechaCreacion ? _fechaCreacion.slice(11, 19) : "—";

  // Detalle de valores / método de recaudación (capturado al crear la orden).
  const _obsTras = (agendamiento as any)?.observaciones || {};
  const _rubros = _obsTras.rubros_cobro || {};
  const esFibrapointsDetalle =
    _rubros.puntos_canjeados != null &&
    `${_rubros.puntos_canjeados}`.trim() !== "" &&
    Number(_rubros.puntos_canjeados) > 0;
  const metodoRecaudacion = esFibrapointsDetalle
    ? "FibraPoints"
    : _obsTras.recaudacion_tecnico
    ? "Recaudación técnico"
    : _obsTras.cargo_a_factura
    ? "Cargo a factura"
    : "—";
  const valorTrasladoDetalle =
    _obsTras.valor_traslado != null && `${_obsTras.valor_traslado}`.trim() !== ""
      ? `$${_obsTras.valor_traslado}`
      : "—";
  const tieneDetalleRecaudacion =
    metodoRecaudacion !== "—" || valorTrasladoDetalle !== "—";

  // Otros conceptos / puntos de red del TRASLADO (catálogo propio de traslados).
  // Editable en En Espera / En Pausa / Agendado (FINALIZADO) / Recoordinado;
  // en los demás estados solo lectura. Se persisten en observaciones al guardar.
  const _estadoAg = (agendamiento?.estado_agendamiento || "").toUpperCase();
  const puedeEditarConceptos = ["EN ESPERA", "EN PAUSA", "FINALIZADO", "RECORDINADO"].includes(_estadoAg);
  const aprobacionConceptos = aprobacionDe(_obsTras);
  // Stock por producto en la ubicación de la flota asignada (referencia).
  const _ubicacionFlotaOp = (agendamiento?.flota_data as any)?.ubicacion;
  const { data: _invStockOp } = useFetchUbicacionProductoSimple({
    enabled: !!_ubicacionFlotaOp,
    params: {
      page: 1,
      page_size: 500,
      estado: true,
      filterByState: false,
      es_para_venta: true,
      ubicacion: _ubicacionFlotaOp,
    } as any,
  });
  const stockPorProductoOp = useMemo(() => {
    const m: Record<string, number> = {};
    const arr = (_invStockOp as any)?.data?.data || [];
    for (const up of arr) {
      const idp = up?.producto_data?.id_producto || up?.id;
      if (idp) m[idp] = Number(up?.cantidad ?? 0);
    }
    return m;
  }, [_invStockOp]);

  ///* constants --------------------
  const [canViewDatosServicio, setCanViewDatosServicio] =
    useState<boolean>(false);
  const [isVisibleCalendario, setIsVisibleCalendario] = useState(false)
  const slot = useSelectDateTimerPreScheduling(s => s.slot);
  const setSelectFloat = useSelectDateTimerPreScheduling(s => s.setSelectFloat);
  const setPlanner = useSelectDateTimerPreScheduling(s => s.setPlanner);

  /*********** Handlers */
  const onEditHorario = async () => {
    // if cancel edit, set flota to db value

    if (isVisibleCalendario) {
      form.reset({
        ...form.getValues(),
        flota: agendamiento?.flota || "",
        flotaRaw: agendamiento?.flota_data || {},
      });

      return
    }

    setSelectFloat(agendamiento?.flota || "");
    setPlanner(agendamiento?.flota || "");
  };

  return (
    <Grid item container spacing={3}>
      <Grid item {...gridSizeMdLg12}
        display={'inline-flex'}
        alignItems={'center'}
      >
        <Icon sx={{ marginRight: 1 }}>
          <Person color="primary" />
        </Icon>
        <TabTextLabel text="Información Personal"
          pt={0}
          pl={0}
          mt={1}
          pb={0}
        />
      </Grid>

      {/* =========== personal info =========== */}
      <>
        <CustomTextField
          name="tipo_identificacion"
          label="Tipo de Identificación"
          control={form.control}
          defaultValue={form.getValues().tipo_identificacion || ""}
          error={errors.tipo_identificacion}
          helperText={errors.tipo_identificacion?.message?.toString() ?? ""}
          shrink={shrink}
          size={gridSizeMdLg6}
          disabled
        />
        <CustomTextField
          name="identificacion"
          label="No. Identificación"
          control={form.control}
          defaultValue={form.getValues().identificacion || ""}
          error={errors.identificacion}
          helperText={errors.identificacion?.message?.toString() ?? ""}
          shrink={shrink}
          size={gridSizeMdLg6}
          disabled
        />
        <CustomTextField
          name="razon_social"
          label="Nombre"
          control={form.control}
          defaultValue={form.getValues().razon_social || ""}
          error={errors.razon_social}
          helperText={errors.razon_social?.message?.toString() ?? ""}
          shrink={shrink}
          disabled
        />

        <CustomTextField
          endAdornmentInput={<TooltipIconEdit />}
          name="persona_referencia_nombre"
          label="Persona de Referencia"
          control={form.control}
          defaultValue={form.getValues().persona_referencia_nombre || ""}
          error={errors.persona_referencia_nombre}
          helperText={errors.persona_referencia_nombre?.message}
          shrink={shrink}
          disabled={isCorrectingData}
        />

        <CustomAutocomplete<any>
          endAdornmentInput={<TooltipIconEdit />}
          size={gridSizeMdLg6}
          label={texto.tipo_parentesco}
          name="persona_referencia_parentesco"
          options={parentescoMockupData || []}
          valueKey="nombre" // name is the fk
          defaultValue={form.getValues().persona_referencia_parentesco || ""}
          isLoadingData={false}
          control={form.control}
          error={errors.persona_referencia_parentesco}
          helperText={errors.persona_referencia_parentesco?.message}
        />

        <CustomCellphoneTextField
          endAdornmentInput={<TooltipIconEdit />}
          name="persona_referencia_celular"
          label="Celular Persona Referencia"
          control={form.control}
          defaultValue={form.getValues().persona_referencia_celular || ""}
          error={errors.persona_referencia_celular}
          helperText={errors.persona_referencia_celular?.message}
          shrink={shrink}
          size={gridSizeMdLg6}
          disabled={isCorrectingData}
        />
      </>

      {/* =========== Datos de Venta =========== */}
      {/* <TabTextLabel text="Fotos del Cliente" pt={2} /> */}

      <Grid item {...gridSizeMdLg12}
        display={'inline-flex'}
        alignItems={'center'}
      >
        <Icon sx={{ marginRight: 1 }}>
          <ImageIcon color="info" />
        </Icon>
        <TabTextLabel text="Fotos del Cliente"
          pt={0}
          pl={0}
          mt={1}
          pb={0}
        />
      </Grid>
      {
        [
          {
            url: agendamiento?.preventa_data?.url_foto_identificacion_delantera,
            alt: "Identificación Frontal"
          },
          {
            url: agendamiento?.preventa_data?.url_foto_identificacion_posterior,
            alt: "Identificación Posterior"
          },
          {
            url: agendamiento?.preventa_data?.url_foto_aceptacion,
            alt: "Foto Aceptación"
          },
          {
            url: agendamiento?.preventa_data?.url_foto_cuenta_bancaria,
            alt: "Cuenta Bancaria"
          },
          {
            url: agendamiento?.preventa_data?.url_foto_ruc,
            alt: "RUC"
          },
          {
            url: agendamiento?.preventa_data?.url_foto_nombramiento,
            alt: "Nombramiento"
          },
        ].filter(e => e.url).map((element) => {

          return <Grid item {...gridSizeMdLg4}>
            <ImagesWithZoom
              src={element.url!}
              alt={element.alt}
            />
            <Typography variant="subtitle2">{element.alt}</Typography>
          </Grid>
        })
      }

      {/* =========== estado llamada =========== */}

      <Grid item {...gridSizeMdLg12}
        display={'inline-flex'}
        alignItems={'center'}
      >
        <Icon sx={{ marginRight: 1 }}>
          <LocalPhoneIcon color="success" />
        </Icon>
        <TabTextLabel text="Estado de la llamada"
          pt={0}
          pl={0}
          mt={1}
          pb={0}
        />
      </Grid>
      <CustomCellphoneTextField
        endAdornmentInput={<TooltipIconEdit />}
        name="celular"
        label="Celular Cliente"
        control={form.control}
        defaultValue={form.getValues().celular || ""}
        error={errors.celular}
        helperText={errors.celular?.message}
        shrink={shrink}
        size={gridSizeMdLg6}
        disabled={isCorrectingData}
      />
      <CustomCellphoneTextField
        endAdornmentInput={<TooltipIconEdit />}
        name="celular_2"
        label="Celular 2 Cliente"
        control={form.control}
        defaultValue={form.getValues().celular_2 || ""}
        error={errors.celular_2}
        helperText={errors.celular_2?.message}
        shrink={shrink}
        size={gridSizeMdLg6}
        disabled={isCorrectingData}
      />
      {/* --------- estado llamada --------- */}
      <CustomAutocompleteArrString
        label="Estado de la llamada"
        name="estado_llamada"
        control={form.control}
        defaultValue={form.getValues().estado_llamada || ""}
        options={ESTADOS_LLAMADAS_CHOICE.filter(
          (el) => el !== EstadosLlamadaEnumChoice.EN_ESPERA
        )}
        isLoadingData={false}
        error={errors.estado_llamada}
        helperText={errors.estado_llamada?.message}
        disableClearable
        disabled={isCorrectingData}
      />
      <CustomTextArea
        label="Observación Llamada"
        name="observacion_llamada"
        control={form.control}
        defaultValue={
          form.getValues()?.observaciones?.observacion_llamada || ""
        }
        error={errors.observacion_llamada}
        helperText={errors.observacion_llamada?.message}
        required={false}
      />



      {/* ===== Detalle de valores / Método de recaudación (de la creación) ===== */}
      {tieneDetalleRecaudacion && (
        <Grid item xs={12}>
          <Box
            sx={{
              p: 2,
              mt: 1,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "rgba(21,101,192,0.03)",
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1565c0", mb: 1 }}>
              Detalle de valores
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Método de recaudación
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {metodoRecaudacion}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Valor traslado
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {valorTrasladoDetalle}
                </Typography>
              </Grid>
              {esFibrapointsDetalle && (
                <>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      FibraPoints antes / canjeados
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {(_rubros.puntos_antes_canje ?? "—")} / {(_rubros.puntos_canjeados ?? "—")}
                    </Typography>
                  </Grid>
                </>
              )}
              {_obsTras.cargo_a_factura && _rubros.meses_pago != null && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Meses a diferir
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {_rubros.meses_pago}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>
      )}

      {/* ===== Otros conceptos + puntos de red del traslado (catálogo de traslados) ===== */}
      <Grid item xs={12}>
        <ConceptosTrasladoSection
          conceptos={(form.watch("otros_conceptos" as any) as any) || []}
          onChangeConceptos={(v) =>
            form.setValue("otros_conceptos" as any, v as any, { shouldDirty: true })
          }
          puntosRed={(form.watch("puntos_red" as any) as any) || []}
          onChangePuntosRed={(v) =>
            form.setValue("puntos_red" as any, v as any, { shouldDirty: true })
          }
          readOnly={!puedeEditarConceptos}
          ubicacionFlota={_ubicacionFlotaOp}
          stockPorProducto={stockPorProductoOp}
          aprobacion={aprobacionConceptos}
          despuesDePuntosRed={
            excedenteFibraDe(_obsTras) && (
              <ExcedenteFibraTraslado excedente={excedenteFibraDe(_obsTras)} modo="lectura" />
            )
          }
          subtitle="Equipos/servicios adicionales que se cobran con el traslado. El técnico podrá completar series y agregar conceptos en sitio; si el traslado ya inició, Activaciones los aprueba."
        />
      </Grid>

      <Grid item {...gridSizeMdLg12}
        display={'inline-flex'}
        alignItems={'center'}
      >
        <Icon sx={{ marginRight: 1 }}>
          <TravelExploreIcon color="primary" />
        </Icon>
        <TabTextLabel text="Ubicación"
          pt={0}
          pl={0}
          mt={1}
          pb={0}
        />
      </Grid>

      {/* ===== Datos de ubicación ANTERIOR (origen del traslado) ===== */}
      <Grid item xs={12}>
        <Box
          sx={{
            p: 2,
            mb: 1,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "rgba(21,101,192,0.03)",
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1565c0", mb: 1 }}>
            Datos de ubicación anterior
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Coordenadas
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {ubicacionAnterior?.coordenadas || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Dirección
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
                {ubicacionAnterior?.direccion || "—"}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Grid>

      {/* ===== Datos de ubicación NUEVA (destino) ===== */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1565c0", mt: 1 }}>
          Datos de ubicación nueva
        </Typography>
      </Grid>

      <AgendamientoOrdenServicioUbicacionContrato
        form={form as any}
        contrato={contrato as any}
        errors={errors}
      />

      <Grid
        item
        xs={12}
        container
        justifyContent={"space-between"}
        alignItems={"center"}
      >

        <Grid item>
          <Typography
            variant="h6"
            sx={{
              display: "flex",
              alignItems: "start",
              justifyContent: "start",
              fontSize: ".9rem",
              color: "#6c737fb0",
            }}
          >
            ACTUALIZAR HORARIOS
          </Typography>
        </Grid>
        {slot == null && (
          <SingleIconButton
            startIcon={<EventRepeatIcon />}
            color={isVisibleCalendario ? "error" : "primary"}
            label={isVisibleCalendario ? "Cancelar edición" : "Editar fecha"}
            onClick={() => {
              setIsVisibleCalendario(!isVisibleCalendario);
              onEditHorario();
            }}
          />
        )}
      </Grid>

      {!isVisibleCalendario && (
        <>
          <CustomTextFieldNoForm
            label="Fecha de instalación"
            value={agendamiento?.fecha_instalacion || "N/A"}
            size={gridSizeMdLg6}
            disabled
          />
          <CustomTextFieldNoForm
            label="Hora de instalación"
            value={
              agendamiento?.hora_instalacion
                ? formatHour(agendamiento?.hora_instalacion)
                : "N/A"
            }
            size={gridSizeMdLg6}
            disabled
          />
          <CustomTextFieldNoForm
            label="Flota"
            value={form.getValues().flotaRaw?.nombre ?? "N/A"}
            size={gridSize}
            disabled
          />
        </>
      )}

      {
        isVisibleCalendario && (
          <Grid item xs={12}>
            <SelectDateTimerSchedulingPyl />
          </Grid>
        )
      }

      {/* {isVisibleCalendario && (
        <CalendarioPreagendamientoPyl
          cacheKey={CacheKeyCalendarioENUM.KEY_CACHE_PREAGENDAMIENTO_PYL}
          form={form}
        />
      )} */}

      {/* ===== Observaciones Recoordinar (comentario del asesor -> viaja al técnico) ===== */}
      <Grid item xs={12}>
        <CustomTextArea
          label="Observaciones Recoordinar"
          name="observacion_recoordinar"
          control={form.control}
          defaultValue={(form.getValues() as any)?.observacion_recoordinar || ""}
          error={(errors as any)?.observacion_recoordinar}
          helperText={(errors as any)?.observacion_recoordinar?.message}
          required={false}
        />
      </Grid>

      {/* ================ Service ================ */}
      {agendamiento?.nap && (
        <TabTexLabelCustomSpace
          textContent="Datos de Servicio"
          showCustomRightSpace={true}
          customRightSpace={
            <SingleButton
              newCustomButton
              startIcon={
                canViewDatosServicio ? <ExpandLessIcon /> : <ExpandMoreIcon />
              }
              label={canViewDatosServicio ? "OCULTAR" : "VER MÁS"}
              variant="text"
              onClick={() => {
                setCanViewDatosServicio((prev) => !prev);
              }}
            />
          }
        />
      )}
      {canViewDatosServicio && (
        <ServiceAndOthersPartPreagendamientoForm
          form={form as any}
          preagendamiento={agendamiento as any}
          isCorrectingData={isCorrectingData}
        />
      )}

      {/* ===== Asesor que creó y agendó el traslado (mismo estilo de la página) ===== */}
      <Grid item xs={12}>
        <Box
          sx={{
            p: 2,
            mt: 1,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "rgba(21,101,192,0.03)",
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1565c0", mb: 1 }}>
            Registrado por
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Asesor
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {asesorCreador}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Fecha
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {fechaCreado}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Hora
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {horaCreado}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Grid>

      {/* Visión 360 del cliente (FibraPoints, saldo/valores, pagos, tickets) — igual que visitas */}
      <AgendamientoOrdenServicioVision360 contrato={contrato} />
    </Grid>
  );
};
export default AgendamientoSingleOrdenServicioTraslado;
